import React, { useState, useEffect } from 'react';
import BarcodeScanner from '../components/BarcodeScanner';
import {
  getProducts,
  getProductByBarcode,
  getCustomers,
  createCustomer,
  updateCustomer,
  getInvoices,
  getInvoice,
  createInvoice,
  addInvoiceItem,
  getInvoicePDF,
  updateInvoiceItemQuantity,
  removeInvoiceItem,
  deleteInvoice,
  getPaymentMethods,
  createPaymentMethod,
  addInvoicePayment,
  reopenInvoice,
} from '../api';

function Invoicing({ user }) {
  const [view, setView] = useState('list'); // list, select-customer, add-items, edit, customers
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState(null); // For the invoice being created
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [showNewPaymentMethod, setShowNewPaymentMethod] = useState(false);
  const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerForm, setEditCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const [invoiceData, setInvoiceData] = useState({
    customer_id: '',
  });
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvoices();
    loadCustomers();
    loadProducts();
    loadPaymentMethods();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await getInvoices();
      setInvoices(response.data);
    } catch (err) {
      console.error('Failed to load invoices');
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await getCustomers();
      setCustomers(response.data);
    } catch (err) {
      console.error('Failed to load customers');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to load products');
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await getPaymentMethods();
      setPaymentMethods(response.data);
    } catch (err) {
      console.error('Failed to load payment methods');
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!newPaymentMethodName.trim()) return;
    try {
      const response = await createPaymentMethod(newPaymentMethodName);
      setPaymentMethods([...paymentMethods, response.data]);
      setPaymentData({ ...paymentData, payment_method: response.data.name });
      setNewPaymentMethodName('');
      setShowNewPaymentMethod(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add payment method');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await addInvoicePayment(selectedInvoice.id, paymentData);
      setMessage('Payment recorded successfully');

      // Reload invoice to get updated data
      const invoiceResponse = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(invoiceResponse.data);

      // Reset form
      setPaymentData({
        amount: '',
        payment_method: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      setShowPaymentForm(false);
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    }
  };

  const handleReopenInvoice = async (invoiceId) => {
    setError('');
    setMessage('');
    try {
      await reopenInvoice(invoiceId);
      setMessage('Invoice reopened successfully');

      // Reload invoice if we're in edit view
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        const response = await getInvoice(invoiceId);
        setSelectedInvoice(response.data);
      }
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen invoice');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await createCustomer(customerForm);
      setCustomers([...customers, response.data]);
      setInvoiceData({ ...invoiceData, customer_id: response.data.id });
      setShowCustomerForm(false);
      setCustomerForm({ name: '', email: '', phone: '', address: '' });
      setMessage('Customer created successfully');
    } catch (err) {
      setError('Failed to create customer');
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditCustomerForm({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
    });
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await updateCustomer(editingCustomer.id, editCustomerForm);
      setMessage('Customer updated successfully');
      setEditingCustomer(null);
      setEditCustomerForm({ name: '', email: '', phone: '', address: '' });
      loadCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update customer');
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await getInvoicePDF(invoiceId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      const response = await getInvoice(invoiceId);
      setSelectedInvoice(response.data);
      setView('edit');
    } catch (err) {
      setError('Failed to load invoice');
    }
  };

  const handleUpdateItemQuantity = async (itemId, newQuantity) => {
    setError('');
    setMessage('');
    try {
      await updateInvoiceItemQuantity(selectedInvoice.id, itemId, newQuantity);
      // Reload the invoice to get updated data
      const response = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setMessage('Quantity updated');
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quantity');
    }
  };

  const handleRemoveInvoiceItem = async (itemId) => {
    setError('');
    setMessage('');
    try {
      await removeInvoiceItem(selectedInvoice.id, itemId);
      // Reload the invoice to get updated data
      const response = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setMessage('Item removed');
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove item');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice? Inventory will be restored.')) {
      return;
    }
    setError('');
    setMessage('');
    try {
      await deleteInvoice(invoiceId);
      setMessage('Invoice deleted successfully');
      setSelectedInvoice(null);
      setView('list');
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete invoice');
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Invoicing</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {view === 'list' ? (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={() => setView('select-customer')}
              >
                Create New Invoice
              </button>
              {user.role === 'admin' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setView('customers')}
                >
                  Manage Customers
                </button>
              )}
            </div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.invoice_number}</td>
                      <td>{invoice.customer_name || 'Walk-in'}</td>
                      <td>${invoice.total.toFixed(2)}</td>
                      <td>${(invoice.amount_paid || 0).toFixed(2)}</td>
                      <td style={{ color: invoice.balance > 0 ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                        ${(invoice.balance || 0).toFixed(2)}
                      </td>
                      <td>
                        <span className={`badge badge-${invoice.status === 'closed' ? 'received' : 'pending'}`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleEditInvoice(invoice.id)}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            View
                          </button>
                          {invoice.status === 'open' && (
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                            >
                              Delete
                            </button>
                          )}
                          {invoice.status === 'closed' && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleReopenInvoice(invoice.id)}
                              style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                            >
                              Reopen
                            </button>
                          )}
                          <button
                            className="btn btn-secondary"
                            onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : view === 'edit' && selectedInvoice ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setView('list');
                setSelectedInvoice(null);
                setShowPaymentForm(false);
              }}
              style={{ marginBottom: '20px' }}
            >
              Back to List
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3>Invoice: {selectedInvoice.invoice_number}</h3>
                <p>Customer: {selectedInvoice.customer_name || 'Walk-in'}</p>
                <p>Status: <span className={`badge badge-${selectedInvoice.status === 'closed' ? 'received' : 'pending'}`}>{selectedInvoice.status}</span></p>
              </div>
              <div style={{ textAlign: 'right', padding: '15px', backgroundColor: selectedInvoice.balance > 0 ? '#fff3cd' : '#d4edda', borderRadius: '8px' }}>
                <p style={{ margin: '5px 0' }}><strong>Total:</strong> ${selectedInvoice.total.toFixed(2)}</p>
                <p style={{ margin: '5px 0' }}><strong>Paid:</strong> ${(selectedInvoice.amount_paid || 0).toFixed(2)}</p>
                <p style={{ margin: '5px 0', fontSize: '1.3rem', color: selectedInvoice.balance > 0 ? '#856404' : '#155724' }}>
                  <strong>Balance:</strong> ${(selectedInvoice.balance || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Items Section - Only editable if open */}
            <h4 style={{ marginTop: '20px', marginBottom: '15px' }}>Items</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                    {selectedInvoice.status === 'open' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>
                        {selectedInvoice.status === 'open' ? (
                          <input
                            type="number"
                            defaultValue={item.quantity}
                            min="1"
                            style={{ width: '70px', padding: '5px' }}
                            onBlur={(e) => {
                              const newQty = parseInt(e.target.value);
                              if (newQty !== item.quantity && newQty > 0) {
                                handleUpdateItemQuantity(item.id, newQty);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                          />
                        ) : (
                          item.quantity
                        )}
                      </td>
                      <td>${item.unit_price.toFixed(2)}</td>
                      <td>${item.total.toFixed(2)}</td>
                      {selectedInvoice.status === 'open' && (
                        <td>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleRemoveInvoiceItem(item.id)}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payment History Section */}
            <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Payment History</h4>
            {selectedInvoice.payments && selectedInvoice.payments.length > 0 ? (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Notes</th>
                      <th>Recorded By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                        <td>{payment.payment_method}</td>
                        <td>${payment.amount.toFixed(2)}</td>
                        <td>{payment.notes || '-'}</td>
                        <td>{payment.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: '#7f8c8d' }}>No payments recorded yet</p>
            )}

            {/* Add Payment Section - Only for open invoices with balance */}
            {selectedInvoice.status === 'open' && selectedInvoice.balance > 0 && (
              <div style={{ marginTop: '20px' }}>
                {!showPaymentForm ? (
                  <button
                    className="btn btn-success"
                    onClick={() => setShowPaymentForm(true)}
                  >
                    Record Payment
                  </button>
                ) : (
                  <div style={{ padding: '15px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '10px' }}>
                    <h4>Record Payment</h4>
                    <form onSubmit={handleAddPayment}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div className="form-group">
                          <label>Amount *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={paymentData.amount}
                            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                            placeholder={`Max: $${selectedInvoice.balance.toFixed(2)}`}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Payment Date *</label>
                          <input
                            type="date"
                            value={paymentData.payment_date}
                            onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Payment Method *</label>
                          {!showNewPaymentMethod ? (
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <select
                                value={paymentData.payment_method}
                                onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                                style={{ flex: 1 }}
                                required
                              >
                                <option value="">-- Select --</option>
                                {paymentMethods.map((method) => (
                                  <option key={method.id} value={method.name}>
                                    {method.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowNewPaymentMethod(true)}
                                style={{ padding: '8px 12px' }}
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <input
                                type="text"
                                value={newPaymentMethodName}
                                onChange={(e) => setNewPaymentMethodName(e.target.value)}
                                placeholder="New payment method"
                                style={{ flex: 1 }}
                              />
                              <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleAddPaymentMethod}
                                style={{ padding: '8px 12px' }}
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  setShowNewPaymentMethod(false);
                                  setNewPaymentMethodName('');
                                }}
                                style={{ padding: '8px 12px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Notes</label>
                          <input
                            type="text"
                            value={paymentData.notes}
                            onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                            placeholder="Optional notes"
                          />
                        </div>
                      </div>
                      <div className="btn-group" style={{ marginTop: '15px' }}>
                        <button type="submit" className="btn btn-success">
                          Save Payment
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowPaymentForm(false);
                            setPaymentData({
                              amount: '',
                              payment_method: '',
                              payment_date: new Date().toISOString().split('T')[0],
                              notes: '',
                            });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="btn-group" style={{ marginTop: '30px' }}>
              {selectedInvoice.status === 'open' && (
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                >
                  Delete Invoice
                </button>
              )}
              {selectedInvoice.status === 'closed' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleReopenInvoice(selectedInvoice.id)}
                >
                  Reopen Invoice
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => downloadPDF(selectedInvoice.id, selectedInvoice.invoice_number)}
              >
                Download PDF
              </button>
            </div>
          </>
        ) : view === 'customers' ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setView('list');
                setEditingCustomer(null);
              }}
              style={{ marginBottom: '20px' }}
            >
              Back to Invoices
            </button>

            <h3 style={{ marginBottom: '20px' }}>Customer Management</h3>

            {editingCustomer ? (
              <div
                style={{
                  padding: '20px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  marginBottom: '20px',
                }}
              >
                <h4>Edit Customer</h4>
                <form onSubmit={handleUpdateCustomer}>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={editCustomerForm.name}
                      onChange={(e) =>
                        setEditCustomerForm({ ...editCustomerForm, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editCustomerForm.email}
                      onChange={(e) =>
                        setEditCustomerForm({ ...editCustomerForm, email: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editCustomerForm.phone}
                      onChange={(e) =>
                        setEditCustomerForm({ ...editCustomerForm, phone: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea
                      value={editCustomerForm.address}
                      onChange={(e) =>
                        setEditCustomerForm({ ...editCustomerForm, address: e.target.value })
                      }
                    />
                  </div>
                  <div className="btn-group">
                    <button type="submit" className="btn btn-success">
                      Save Changes
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setEditingCustomer(null);
                        setEditCustomerForm({ name: '', email: '', phone: '', address: '' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', color: '#7f8c8d' }}>
                          No customers yet
                        </td>
                      </tr>
                    ) : (
                      customers.map((customer) => (
                        <tr key={customer.id}>
                          <td>{customer.name}</td>
                          <td>{customer.email || '-'}</td>
                          <td>{customer.phone || '-'}</td>
                          <td>{customer.address || '-'}</td>
                          <td>
                            <button
                              className="btn btn-primary"
                              onClick={() => handleEditCustomer(customer)}
                              style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : view === 'select-customer' ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setView('list');
                setInvoiceData({ customer_id: '' });
                setShowCustomerForm(false);
              }}
              style={{ marginBottom: '20px' }}
            >
              Back to List
            </button>

            <h3 style={{ marginBottom: '20px' }}>Step 1: Select Customer</h3>

            <div className="form-group">
              <label>Customer</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  value={invoiceData.customer_id}
                  onChange={(e) =>
                    setInvoiceData({ ...invoiceData, customer_id: e.target.value })
                  }
                  style={{ flex: 1 }}
                >
                  <option value="">-- Walk-in Customer --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCustomerForm(!showCustomerForm)}
                >
                  New
                </button>
              </div>
            </div>

            {showCustomerForm && (
              <div
                style={{
                  padding: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  marginBottom: '20px',
                }}
              >
                <h4>New Customer</h4>
                <form onSubmit={handleCreateCustomer}>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={customerForm.name}
                      onChange={(e) =>
                        setCustomerForm({ ...customerForm, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={customerForm.email}
                      onChange={(e) =>
                        setCustomerForm({ ...customerForm, email: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={customerForm.phone}
                      onChange={(e) =>
                        setCustomerForm({ ...customerForm, phone: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea
                      value={customerForm.address}
                      onChange={(e) =>
                        setCustomerForm({ ...customerForm, address: e.target.value })
                      }
                    />
                  </div>
                  <div className="btn-group">
                    <button type="submit" className="btn btn-success">
                      Create Customer
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCustomerForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={async () => {
                setError('');
                setMessage('');
                try {
                  // Create invoice with selected customer (no items yet)
                  const response = await createInvoice({
                    customer_id: invoiceData.customer_id || null,
                    items: [],
                  });
                  setNewInvoice(response.data);
                  setView('add-items');
                  setShowCustomerForm(false);
                } catch (err) {
                  setError(err.response?.data?.error || 'Failed to create invoice');
                }
              }}
              style={{ marginTop: '20px' }}
            >
              Continue to Add Items
            </button>
          </>
        ) : view === 'add-items' && newInvoice ? (
          showScanner ? (
            <BarcodeScanner
              onScan={async (barcode) => {
                setShowScanner(false);
                try {
                  const productResponse = await getProductByBarcode(barcode);
                  const product = productResponse.data;

                  // Add item to invoice via API
                  const response = await addInvoiceItem(newInvoice.id, {
                    product_id: product.id,
                    quantity: 1,
                    unit_price: product.price,
                  });
                  setNewInvoice(response.data);
                  setMessage(`Added ${product.name} to invoice`);
                  loadProducts(); // Refresh product stock
                } catch (err) {
                  setError(err.response?.data?.error || 'Product not found');
                }
              }}
              onClose={() => setShowScanner(false)}
            />
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setView('list');
                  setNewInvoice(null);
                  setInvoiceData({ customer_id: '' });
                  loadInvoices();
                }}
                style={{ marginBottom: '20px' }}
              >
                Done - Back to List
              </button>

              <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 10px 0' }}>Invoice: {newInvoice.invoice_number}</h3>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Customer:</strong> {newInvoice.customer_name || 'Walk-in Customer'}
                </p>
                {newInvoice.email && (
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    <strong>Email:</strong> {newInvoice.email}
                  </p>
                )}
                {newInvoice.phone && (
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    <strong>Phone:</strong> {newInvoice.phone}
                  </p>
                )}
              </div>

              <h4 style={{ marginBottom: '15px' }}>Add Items</h4>

              <div className="btn-group" style={{ marginBottom: '20px' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowScanner(true)}
                >
                  Scan Barcode
                </button>
              </div>

              <div className="form-group">
                <label>Or Add Manually</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select id="manual-product-new" style={{ flex: 1 }}>
                    <option value="">-- Select Product --</option>
                    {products.filter(p => p.quantity > 0).map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ${product.price?.toFixed(2) || '0.00'} (Stock: {product.quantity})
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-secondary"
                    onClick={async () => {
                      const productId = document.getElementById('manual-product-new').value;
                      if (!productId) return;

                      const product = products.find((p) => p.id === parseInt(productId));
                      if (!product) return;

                      setError('');
                      try {
                        const response = await addInvoiceItem(newInvoice.id, {
                          product_id: product.id,
                          quantity: 1,
                          unit_price: product.price,
                        });
                        setNewInvoice(response.data);
                        setMessage(`Added ${product.name} to invoice`);
                        loadProducts(); // Refresh product stock
                      } catch (err) {
                        setError(err.response?.data?.error || 'Failed to add item');
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Items</h4>

              {newInvoice.items.length === 0 ? (
                <p style={{ color: '#7f8c8d' }}>No items added yet</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Total</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newInvoice.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.name}</td>
                          <td>
                            <input
                              type="number"
                              defaultValue={item.quantity}
                              min="1"
                              style={{ width: '70px', padding: '5px' }}
                              onBlur={async (e) => {
                                const newQty = parseInt(e.target.value);
                                if (newQty !== item.quantity && newQty > 0) {
                                  try {
                                    await updateInvoiceItemQuantity(newInvoice.id, item.id, newQty);
                                    const response = await getInvoice(newInvoice.id);
                                    setNewInvoice(response.data);
                                    loadProducts();
                                  } catch (err) {
                                    setError(err.response?.data?.error || 'Failed to update quantity');
                                    e.target.value = item.quantity;
                                  }
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                              }}
                            />
                          </td>
                          <td>${item.unit_price.toFixed(2)}</td>
                          <td>${item.total.toFixed(2)}</td>
                          <td>
                            <button
                              className="btn btn-danger"
                              onClick={async () => {
                                try {
                                  await removeInvoiceItem(newInvoice.id, item.id);
                                  const response = await getInvoice(newInvoice.id);
                                  setNewInvoice(response.data);
                                  setMessage('Item removed');
                                  loadProducts();
                                } catch (err) {
                                  setError(err.response?.data?.error || 'Failed to remove item');
                                }
                              }}
                              style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {newInvoice.items.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'right' }}>
                  <p>
                    <strong>Subtotal:</strong> ${newInvoice.subtotal.toFixed(2)}
                  </p>
                  <p>
                    <strong>Tax (8%):</strong> ${newInvoice.tax.toFixed(2)}
                  </p>
                  <p style={{ fontSize: '1.2rem' }}>
                    <strong>Total:</strong> ${newInvoice.total.toFixed(2)}
                  </p>
                </div>
              )}
            </>
          )
        ) : null}
      </div>
    </div>
  );
}

export default Invoicing;
